import { CareMomentsRankId } from "../../messages";
import { CareMomentRank, CareMomentRankChange, Midpoint } from "./CareMoments.types";

export function getRoadFromAndToPercentage(params: {
  rankIds: CareMomentsRankId[];
  rank: CareMomentRankChange;
  road: {
    totalLength: number;
    segmentWidth: number;
    curveStrength: number;
    segmentHeight: number;
    verticalCurvedLineLength: number;
  };
}) {
  return {
    fromProgress: calculateRoadPercentage({
      totalLength: params.road.totalLength,
      numRanks: params.rankIds.length,
      currentRank: params.rankIds.indexOf(params.rank.from.rank.id),
      nextRankPercentage: params.rank.from.nextRankPercents,
      segmentWidth: params.road.segmentWidth,
      curveStrength: params.road.curveStrength,
      segmentHeight: params.road.segmentHeight,
      verticalCurvedLineLength: params.road.verticalCurvedLineLength,
    }),
    toProgress: calculateRoadPercentage({
      totalLength: params.road.totalLength,
      numRanks: params.rankIds.length,
      currentRank: params.rankIds.indexOf(params.rank.to.rank.id),
      nextRankPercentage: params.rank.to.nextRankPercents,
      segmentWidth: params.road.segmentWidth,
      curveStrength: params.road.curveStrength,
      segmentHeight: params.road.segmentHeight,
      verticalCurvedLineLength: params.road.verticalCurvedLineLength,
    }),
  };
}

function calculateRoadPercentage(params: {
  totalLength: number;
  numRanks: number;
  currentRank: number;
  nextRankPercentage: number;
  segmentWidth: number;
  curveStrength: number;
  segmentHeight: number;
  verticalCurvedLineLength: number;
}) {
  const horizontalLineLength = params.segmentWidth - 2 * params.curveStrength;
  const quarterCircleLength = params.verticalCurvedLineLength / 2;

  const firstSegmentLength = horizontalLineLength + params.curveStrength + quarterCircleLength;
  const lastSegmentLength =
    horizontalLineLength * 2 + params.verticalCurvedLineLength + quarterCircleLength;

  // Calculate the length of the middle segments
  const middleSegmentLength =
    params.numRanks > 2
      ? (params.totalLength - firstSegmentLength - lastSegmentLength) / (params.numRanks - 2)
      : 0;

  if (params.numRanks === params.currentRank + 1) {
    return 100;
  }

  const completedPathLength = params.currentRank * middleSegmentLength + firstSegmentLength;
  const completedPathPercentage = (completedPathLength / params.totalLength) * 100;
  const nextRankContribution =
    (((params.nextRankPercentage / 100) * middleSegmentLength) / params.totalLength) * 100;

  return completedPathPercentage + nextRankContribution;
}

export function getNextRank(params: { ranks: CareMomentRank[]; currentRankId: CareMomentsRankId }) {
  const currentRankIndex = params.ranks.findIndex((rank) => rank.id === params.currentRankId);
  return currentRankIndex === params.ranks.length - 1 ? null : params.ranks[currentRankIndex + 1];
}

export function calculateProgressIncrement(params: { stepDuration: number; numSteps: number }) {
  const duration = params.stepDuration * params.numSteps;
  const framesPerSecond = 60; // Assuming 60 frames per second
  const totalFrames = (duration / 1000) * framesPerSecond;
  return 100 / totalFrames;
}

export function getRankChangeType(params: {
  rankChange: CareMomentRankChange;
  ranks: CareMomentRank[];
}) {
  const currentRankIndex = params.ranks.findIndex(
    (rank) => rank.id === params.rankChange.to.rank.id
  );
  const previousRankIndex = params.ranks.findIndex(
    (rank) => rank.id === params.rankChange.from.rank.id
  );

  if (currentRankIndex === previousRankIndex) {
    return params.rankChange.to.points === 0 &&
      params.rankChange.from.nextRankPercents > params.rankChange.to.nextRankPercents
      ? "streak-break"
      : "none";
  }

  return currentRankIndex > previousRankIndex ? "promotion" : "demotion";
}

export function increaseProgress({
  avatar,
  progressPath,
  totalLength,
  direction,
  progressIncrement,
  toProgress,
  scaffold,
  midpoints,
  prevProgress,
  onFinishAnimation,
}: {
  progressPath: SVGPathElement;
  totalLength: number;
  avatar: HTMLDivElement;
  direction: 1 | -1;
  progressIncrement: number;
  toProgress: number;
  scaffold: HTMLDivElement;
  midpoints: Midpoint[];
  prevProgress: number;
  onFinishAnimation: () => void;
}) {
  const capFunction = direction === 1 ? Math.min : Math.max;
  const progress = capFunction(prevProgress + progressIncrement * direction, toProgress);

  // Calculate the stroke-dashoffset based on progress
  const progressOffset = totalLength - (progress / 100) * totalLength;
  progressPath.style.strokeDashoffset = progressOffset.toString();

  // Calculate avatar position along the SVG path
  const point = progressPath.getPointAtLength(totalLength - progressOffset);

  // Adjust avatar position
  const avatarX = point.x - avatar.offsetWidth / 2;
  const avatarY = point.y - avatar.offsetHeight / 2;
  avatar.style.transform = `translate(${avatarX}px, ${avatarY}px)`;

  const avatarYComparedToScaffold = avatarY - scaffold.clientHeight / 2 + avatar.offsetHeight / 2;
  scaffold.scrollTo({
    top: Math.max(0, avatarYComparedToScaffold),
    behavior: "auto",
  });

  // Check if avatar has reached each circle and change color if so
  midpoints.forEach((midpoint, index) => {
    const hasReachedMovingForward = direction === 1 && point.y >= midpoint.y - 10;
    const hasReachedMovingBackward = direction === -1 && point.y <= midpoint.y + 10;

    if (hasReachedMovingForward || hasReachedMovingBackward) {
      const circle = document.getElementById("circle" + index) as SVGCircleElement | null;

      const color = hasReachedMovingForward ? "white" : "#3C83F6";

      circle?.setAttribute("fill", color);
    }
  });

  if ((direction === 1 && progress >= toProgress) || (direction === -1 && progress <= toProgress)) {
    onFinishAnimation();
    return 100;
  }

  return progress;
}

export function calculateVerticalCurvedLineLength({
  curveStrength,
  segmentHeight,
}: {
  curveStrength: number;
  segmentHeight: number;
}) {
  const svgNS = "http://www.w3.org/2000/svg";

  const myPath = document.createElementNS(svgNS, "path");

  const x = 0;

  const { cx1, cy1, cx2, cy2, y } = getControlPointsForCurvedVerticalLine({
    curveStrength,
    direction: 1,
    segmentHeight,
    x,
    y: 0,
  });

  myPath.setAttribute("d", `M0,0 C${cx1},${cy1} ${cx2},${cy2} ${x},${y}`);

  return myPath.getTotalLength();
}

export function getControlPointsForCurvedVerticalLine({
  curveStrength,
  direction,
  segmentHeight,
  x,
  y,
}: {
  curveStrength: number;
  direction: number;
  segmentHeight: number;
  x: number;
  y: number;
}) {
  // Calculate control points for the curve
  const cx1 = x + curveStrength * direction; // Control point 1 x-coordinate
  const cy1 = y; // Control point 1 y-coordinate (same as the y of the starting point)
  const cx2 = x + curveStrength * direction; // Control point 2 x-coordinate
  const cy2 = y + segmentHeight; // Control point 2 y-coordinate (same as the y of the end point)
  y += segmentHeight;

  return {
    cx1,
    cy1,
    cx2,
    cy2,
    y,
  };
}
